
import MarkerCluster from '@google/markerclusterer';
import googleMapsLoader from '~/services/googleMapsLoader';
import Grid, { zoomRanges } from '~/services/map/grid';
import InteractiveMapFilters from '~/components/map/InteractiveMapFilters.vue';
import InteractiveMapReport from '~/components/map/InteractiveMapReport.vue';

export default {
    name: 'InteractiveMap',
    components: {
        InteractiveMapFilters,
        InteractiveMapReport,
    },
    props: {
        /**
         * Set map inital render to specific location and zoom.
         */
        initialLocation: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            bounds: {},
            grid: null,
            initialLoad: true,
            isZoomTrigger: false,
            markers: {
                country: [],
                county: [],
            },
            locationType: '',
            newLocationType: '',
            maparea: null,
            mapElement: '',
            mapId: 'map',
            reportEndpoint: this.$config.endpoints.report,
            reportFilters: {
                /**
                 * Allow users to change map filters.
                 *
                 * type -> open || closed || all
                 * days -> null || 30 || 60 || 90 (days)
                 *
                 */
                type: 'open',
                days: null,
            },
            reports: [],
            selectedReport: {},
            showReportsLevel: 'city',
            showInfoPanel: false,
            markerCluster: null,
            zoom: this.isMobile ? 5 : 6,
        };
    },
    computed: {
        isMobile() {
            return this.$store.state.isMobile || (typeof window !== 'undefined' && window.innerHeight < 620);
        },
        countMarkerImage() {
            return {
                url: `${this.$config.cdn_domain}/images/map/counter-pin-m1-${this.pinType}.png`,
                width: 51,
                height: 67,
            };
        },
        /**
         * The currently selected locationType array of markers.
         *
         * Return empty array if report markers are selected.
         *
         * @return array
         */
        locationMarkers() {
            if (this.locationType === 'country' || this.locationType === 'county') {
                return this.markers[this.locationType];
            }
            return [];
        },
        reportMarkerImage() {
            return {
                url: `${this.$config.cdn_domain}/images/map/report-pin-s1-${this.pinType}.png`,
                width: 31,
                height: 41,
            };
        },
        /**
         * Change map marker icon based on open or closed reports.
         *
         * @return string
         */
        pinType() {
            if (this.reportFilters.type === 'open') {
                return 'light';
            }
            // closed reports use grey pins
            return 'grey';
        },
        setMapHeight() {
            return this.isMobile ? 'height: calc(100vh - 96px)' : 'height: calc(100vh - 167px)';
        },
    },
    watch: {
        locationType(newVal, oldVal) {
            if (!this.markerCluster) {
                return;
            }
            if ((newVal === 'county' && oldVal === 'city') || (newVal === 'city' && oldVal === 'county')) {
                this.clearMarkerCluster();
            }
        },
    },
    mounted() {
        this.mapElement = document.getElementById(this.mapId);

        if (this.initialLocation && this.initialLocation.closed) {
            this.reportFilters = {
                type: 'closed',
                days: 90,
            };
        }

        this.initMapRender();
    },
    methods: {
        initMapRender() {
            if (this.$store.state.googleMaps.isReady) {
                this.loadMap();
            } else {
                googleMapsLoader(this.$store, () => {
                    this.loadMap();
                });
            }
        },
        mapOptions() {
            const init = this.getInitialCenter();
            return {
                mapTypeId: 'roadmap',
                zoom: init.zoom,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: !this.isMobile,
                center: {
                    lat: init.center.lat,
                    lng: init.center.lng,
                },
                gestureHandling: 'greedy',
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [
                            { visibility: 'off' },
                        ],
                    },
                ],
            };
        },
        /**
         * Set initial location and zoom level via prop or return default.
         *
         * @return object
         */
        getInitialCenter() {
            if (this.initialLocation
                && this.initialLocation.zoomType
                && this.initialLocation.lat !== null
                && this.initialLocation.lng !== null) {
                const initZoom = zoomRanges.find((zoom) => zoom.location === this.initialLocation.zoomType).range;
                return {
                    zoom: initZoom,
                    center: {
                        lat: this.initialLocation.lat,
                        lng: this.initialLocation.lng,
                    },
                };
            }
            // Set defaults
            return {
                zoom: this.zoom,
                center: {
                    lat: 54.4096267,
                    lng: -2.912219,
                },
            };
        },
        loadMap() {
            this.maparea = new window.google.maps.Map(this.mapElement, this.mapOptions());

            window.google.maps.event.addListener(this.maparea, 'idle', () => {
                this.limitChunkRequests();
            });
        },
        /**
         * Process map events through checks before calling xhr requests to get more markers.
         *
         * If markers required, show stored markers or get more for current chunk.
         */
        limitChunkRequests() {
            this.checkLocationType();
            this.zoom = this.maparea.getZoom();
            const { isZoomTrigger, locationMarkers } = this;

            if (!this.initialLoad && isZoomTrigger && locationMarkers.length) {
                this.showLocationMarkers(locationMarkers);
            } else if (this.initialLoad || isZoomTrigger || this.outsideOfChunk()) {
                this.getChunk();
                this.initialLoad = false;
            }
            this.isZoomTrigger = false;
        },
        /**
         * Check if locationType has changed.
         *
         * if newLocationType check if zoom trigger && update locationType with new location.
         */
        checkLocationType() {
            if (!this.grid) {
                return;
            }
            this.newLocationType = this.grid.getLocationType();

            if (this.newLocationType !== this.locationType) {
                this.checkIsZoomTrigger();
                this.locationType = this.newLocationType;
            }
        },
        /**
         * If locationType changes set zoom trigger flag.
         */
        checkIsZoomTrigger() {
            // Do not trigger zoom refresh if zooming in && we already have report data
            if (this.locationType === this.showReportsLevel && this.newLocationType === 'road') {
                this.isZoomTrigger = false;
            } else {
                if (this.newLocationType !== 'city') {
                    this.clearMarkers(this.locationType);
                }
                this.isZoomTrigger = true;
            }
        },
        /**
         * Call grid to get markers for current chunk.
         */
        getChunk() {
            const getForLocationType = this.locationType;
            this.grid = new Grid(this.$config, this.$axios, this.maparea, this.reportFilters, (chunk) => {
                this.hideLocationMarkers(this.markers.county);
                if (this.locationType !== getForLocationType) {
                    // abort data if locationType changes before xhr response
                    return;
                }
                if (!this.locationType) {
                    this.locationType = this.grid.getLocationType();
                }
                this.setMarkers(chunk);
            });
        },
        /**
         * Filter out existing markers and set new markers.
         */
        setMarkers(chunk) {
            if (this.grid.isShowReportsLevel && chunk.reports) {
                chunk.reports.forEach((newReport) => {
                    const alreadySet = this.reports.filter((report) => report.id === newReport.id).length;
                    if (!alreadySet) {
                        this.setReportMarker(newReport);
                    }
                });
                this.setMarkerCluster();
            } else if (chunk.locations) {
                chunk.locations.forEach((newLocation, index) => {
                    this.setLocationMarker(newLocation, index);
                });
            }
        },
        /**
         * Format and set location marker before storing locally.
         */
        setLocationMarker(newLocation, index) {
            const marker = {
                id: newLocation.id,
                name: newLocation.name ? newLocation.name : null,
                reports_count: newLocation.reports_count.toString(),
                lat: newLocation.lat,
                lng: newLocation.lon,
            };

            marker.marker = new window.google.maps.Marker({
                position: {
                    lat: marker.lat,
                    lng: marker.lng,
                },
                map: this.maparea,
                icon: this.markerIcon(),
                label: {
                    text: this.countFormatter(marker.reports_count),
                    fontSize: '20px',
                },
                title: marker.name ? marker.name : marker.reports_count,
                zindex: index,
            });

            marker.marker.addListener('click', () => {
                this.setZoom(marker.marker);
            });

            this.markers[this.locationType].push(marker);
        },
        /**
         * Only attempt to get more reports if viewport has been moved over a set amount.
         */
        outsideOfChunk() {
            if (!this.grid) {
                return true;
            }
            if (this.locationType === 'country' || this.locationType === 'county') {
                return false;
            }
            const newLocation = this.maparea.getCenter();
            const newLat = newLocation.lat();
            const newLon = newLocation.lng();
            const { origin } = this.grid;
            const latOffset = (origin.lat2 - origin.lat1) / 4;
            const lonOffset = (origin.lon2 - origin.lon1) / 4;

            if (newLat < (origin.lat1 + latOffset) || newLat > (origin.lat2 - latOffset)
                || newLon < (origin.lon1 + lonOffset) || newLon > (origin.lon2 - lonOffset)
            ) {
                return true;
            }
            return false;
        },
        /**
         * If locationType, clear / hide old markers of locationType.
         *
         * If no locationType, hard reset markers and clear all locationMarkers.
         * This is to use report filters i.e. switch closed / open reports.
         */
        clearMarkers(locationType = null) {
            if (locationType === null || locationType === 'city' || locationType === 'road') {
                while (this.reports.length) {
                    this.reports.pop().marker.setMap(null);
                }
            }

            if (locationType === null) {
                while (this.markers.country.length) {
                    this.markers.country.pop().marker.setMap(null);
                }

                while (this.markers.county.length) {
                    this.markers.county.pop().marker.setMap(null);
                }
            } else {
                this.hideLocationMarkers(this.locationMarkers);
            }
        },
        /**
         * Show location marker from already stored markers.
         */
        showLocationMarkers(markers) {
            markers.forEach((location) => {
                location.marker.setMap(this.maparea);
            });
        },
        /**
         * Hide location marker but keep as a stored marker.
         */
        hideLocationMarkers(markers) {
            markers.forEach((location) => {
                location.marker.setMap(null);
            });
        },
        /**
         * If reports counter > 1,000 trim to 1K etc.
         *
         * @return string | integer
         */
        countFormatter(count) {
            if (count >= 1000) {
                count = count.toString(10);
                const split = count.substring(0, (count.length - 3));
                return `${split}K`;
            }
            return count;
        },
        /**
         * Format and set report marker before storing locally.
         */
        setReportMarker(report) {
            const icon = this.reportMarkerImage;

            report.marker = new window.google.maps.Marker({
                position: {
                    lat: report.lat,
                    lng: report.lon,
                },
                map: this.maparea,
                icon: {
                    url: icon.url,
                    size: new window.google.maps.Size(icon.width, icon.height),
                },
                title: report.road ? report.road.name : '',
            });

            report.marker.addListener('click', () => {
                this.showReport(report.id);
            });

            this.reports.push(report);
        },
        async showReport(id) {
            if (this.selectedReport && this.selectedReport.id === id) {
                this.showInfoPanel = true;
                return;
            }

            const reportUrl = `${this.reportEndpoint}/${id}`;

            const { report } = await this.$axios.$get(reportUrl);

            this.selectedReport = report;
            this.showInfoPanel = true;
        },
        /**
         * Set map zoom and pan to new central position.
         */
        setZoom(marker, getPosition = true) {
            let position;
            if (getPosition) {
                position = marker.getPosition();
            } else {
                position = {
                    lat: marker.lat,
                    lng: marker.lng ? marker.lng : marker.lon,
                };
            }
            this.maparea.setZoom(this.nextZoom());
            this.maparea.panTo(position);
        },
        /**
         * Get the next zoom level from current locationType level.
         *
         * @return integer
         */
        nextZoom() {
            const { zooms } = this.grid;
            let nextZoom = this.grid.zoom;

            zooms.forEach((zoom) => {
                if (this.locationType === zoom.location && this.locationType !== 'road') {
                    // return next iteration zoom level
                    nextZoom = zoom.range + 1;
                }
            });
            return nextZoom;
        },
        markerIcon() {
            const icon = this.countMarkerImage;
            return {
                url: icon.url,
                size: new window.google.maps.Size(icon.width, icon.height),
                labelOrigin: new window.google.maps.Point(25, 25),
            };
        },
        /**
         * Enable report markers to cluster together on map.
         */
        setMarkerCluster() {
            if (this.markerCluster) {
                this.clearMarkerCluster();
            }

            const icon = this.countMarkerImage;
            const markers = [];
            this.reports.forEach((report) => {
                markers.push(report.marker);
            });

            this.markerCluster = new MarkerCluster(this.maparea, markers, {
                styles: [
                    {
                        url: icon.url,
                        height: icon.height + 16,
                        width: icon.width,
                        anchor: [15, 0],
                        textColor: '#000000',
                        textSize: 20,
                    },
                ],
            });
        },
        /**
         * Clear marker cluser.
         */
        clearMarkerCluster() {
            this.markerCluster.removeMarkers(this.markerCluster.getMarkers());
        },
        /**
         * Clear all markers then get new with applied filters.
         */
        filterChanged() {
            this.clearMarkers();
            this.markers.country = [];
            this.markers.county = [];

            this.getChunk();
        },
    },
};
